import store from '@/store/store.js'
import units from '@/constants/timeUnits'

export const relativeTime = (date, unit, n = Infinity) => {
  const locale = store.getters['auth/locale']
  const elapsedMilliseconds = date - new Date()
  const elapsedUnits = Math.ceil(elapsedMilliseconds / units[unit])

  const rtl = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const dtf = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' })

  if (Math.abs(elapsedUnits) < n) {
    return rtl.format(elapsedUnits, unit)
  }

  return dtf.format(date)
}

export const relativeTimeComplex = (date1, date2 = new Date()) => {
  const locale = store.getters['auth/locale']
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const elapsed = date1 - date2

  // "Math.abs" accounts for both "past" & "future" scenarios
  for (let unit in units) {
    if (Math.abs(elapsed) > units[unit] || unit == 'second') {
      return rtf.format(Math.ceil(elapsed / units[unit]), unit)
    }
  }
}

export const localTime = date => {
  const locale = store.getters['auth/locale']
  const dtf = new Intl.DateTimeFormat(locale , {timeStyle: "short"})

  return dtf.format(date)
}

export const localDate = date => {
  const locale = store.getters['auth/locale']
  const dtf = new Intl.DateTimeFormat(locale , { dateStyle: "short" })

  return dtf.format(date)
}

export const localDateShort = date => {
  const locale = store.getters['auth/locale']
  const dtf = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' })

  return dtf.format(date)
}

export const localDateTime = date => {
  return `${localTime(date)} ${localDate(date)}`
}

export const localDateTimeMedium = date => {
  const locale = store.getters['auth/locale']
  const dtf = new Intl.DateTimeFormat(locale , {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  })

  return dtf.format(date).replaceAll(',', '')
}

export const isToday = date => {
  const dt = new Date()
  return date === (new Date(dt.getTime() - (dt.getTimezoneOffset()*60*1000)).toISOString().split('T')[0])
}

export const dateDiffFromNow = (d, unit) => {
  const date = new Date(d)
  const now = new Date()

  return Math.abs(Math.ceil((date - now) / units[unit]))
}

export const durationTime = (seconds) => {
  return new Date(seconds * units.second).toISOString().substr(11, 8)
}

export const isDatesEquals = (d1, d2) => {
  return new Date(d1).setHours(0,0,0,0) === new Date(d2).setHours(0,0,0,0)
}

export const isDateTimeEquals = (d1, d2) => {
  return new Date(d1).getTime() === new Date(d2).getTime()
}

// Sunday - Saturday : 0 - 6
export const getDayOfTheWeek = (date) => {
  return  date ? new Date(date).getDay() : new Date().getDay()
}

// 0 - 31
export const getDayOfTheMonth = (date) => {
  return  date ? new Date(date).getDate() : new Date().getDate()
}

export const getDayNameByIndex = (dayIndex, weekday = 'short') => {
  const locale = store.getters['auth/locale']
  const date = _.isNil(dayIndex) ? new Date() : new Date(0, 0, dayIndex)

  return date.toLocaleString(locale, { weekday })
}

export const getDayNameFromTimestamp = (timestamp, weekday = 'short') => {
  const locale = store.getters['auth/locale']
  const date = new Date(timestamp)

  return new Intl.DateTimeFormat(locale, { weekday }).format(date)
}

export const startOfDay = ({ local = false } = {}) => {
  const date = new Date()
  date.setHours(0,0,0,0)

  return local ? localTime(date) : date
}

export const endOfDay = ({ local = false } = {}) => {
  const date = new Date()
  date.setHours(23,59,59,999)

  return local ? localTime(date) : date
}

// 2021-01-05
export const getISODate = (date) => {
  return date
  ? new Date(date).toISOString().replace(/T.*/,'').split('-').join('-')
  : new Date().toISOString().replace(/T.*/,'').split('-').join('-')
}

export const subtractMonth = (d, n) => {
  const date = d ? new Date(d) : new Date()

  return date.setMonth(date.getMonth() - n)
}

export const subtractWeek = (d, n) => {
  const date = d ? new Date(d) : new Date()

  return date.setDate(date.getDate() - (n * 6))
}

// ['12 AM', "1 AM', '2 AM', '3 AM', ...]
export const generateTimeSeries = () => {
  const locale = store.getters['auth/locale']
  const dt = new Date(1970, 0, 1)
  const rc = []

  while (dt.getDate() === 1) {
      rc.push(new Intl.DateTimeFormat(locale, {hour: 'numeric'}).format(dt))
      dt.setMinutes(dt.getMinutes() + 60)
  }

  return rc
}
